class ProgressLine extends HTMLElement {
  connectedCallback() {
    try {
      this.render();
    } catch (error) {
      // Fail silently to avoid breaking the cart drawer if anything goes wrong
      console.error('[progress-line] render error', error);
    }
  }

  /**
   * Parse tiers JSON safely and normalize structure
   * @returns {{min:number, discount:number}[]}
   */
  parseTiers() {
    const raw = this.dataset.tiers || '[]';

    const decodeEntities = (str) => {
      const ta = document.createElement('textarea');
      ta.innerHTML = str;
      return ta.value;
    };

    const parseTierString = (value) => {
      // Accept formats like "3-10%" or "3–10%" (en dash) with optional spaces
      const match = String(value).trim().match(/(\d+)\s*[–-]\s*(\d+)%/);
      if (!match) return null;
      return { min: Number(match[1]), discount: Number(match[2]) };
    };

    const fromObjectArray = (arr) => arr
      .map((t) => ({ min: Number(t.min) || 0, discount: Number(t.discount) || 0 }))
      .filter((t) => t.min > 0)
      .sort((a, b) => a.min - b.min);

    const fromStringArray = (arr) => arr
      .map(parseTierString)
      .filter(Boolean)
      .sort((a, b) => a.min - b.min);

    const fromLooseString = (str) => {
      const results = [];
      const regex = /(\d+)\s*[–-]\s*(\d+)%/g;
      let m;
      while ((m = regex.exec(str)) !== null) {
        results.push({ min: Number(m[1]), discount: Number(m[2]) });
      }
      return results.sort((a, b) => a.min - b.min);
    };

    let tiers = [];
    const decoded = decodeEntities(raw);
    try {
      const parsed = JSON.parse(decoded);
      if (Array.isArray(parsed)) {
        if (parsed.length > 0 && typeof parsed[0] === 'string') {
          tiers = fromStringArray(parsed);
        } else {
          tiers = fromObjectArray(parsed);
        }
      } else if (typeof parsed === 'string') {
        tiers = fromLooseString(parsed);
      }
    } catch (_e) {
      // If JSON parsing fails, parse as a loose string
      tiers = fromLooseString(decoded);
    }

    return tiers;
  }

  /**
   * Compute key values used to render the UI
   */
  computeState() {
    const qty = Math.max(0, Number(this.dataset.qty) || 0);
    const tiers = this.parseTiers();
    const maxStep = tiers.length > 0 ? tiers[tiers.length - 1].min : Math.max(qty, 1);
    return { qty, tiers, maxStep };
  }

  /**
   * Build blocks HTML underneath the track
   */
  buildBlocks(qty, maxStep) {
    const blocks = [];
    for (let i = 1; i <= maxStep; i += 1) {
      const isActive = i <= qty;
      const pct = Math.min(100, Math.max(0, (i / maxStep) * 100));
      blocks.push(
        `<div class="progress-block${isActive ? ' progress-block--active' : ''}" style="left: ${pct}%" aria-hidden="true">` +
          `<div class="progress-block__bar"></div>` +
          `<div class="progress-block__label">${i}</div>` +
        `</div>`
      );
    }
    return blocks.join('');
  }

  /**
   * Build tier labels above the track
   */
  buildTierLabels(qty, maxStep, tiers) {
    return tiers
      .map((tier) => {
        const pct = Math.min(100, Math.max(0, (tier.min / maxStep) * 100));
        const active = qty >= tier.min;
        return `<div class="progress-tier-label${active ? ' progress-tier-label--active' : ''}" style="left: ${pct}%">-${tier.discount}%</div>`;
      })
      .join('');
  }

  render() {
    const { qty, tiers, maxStep } = this.computeState();

    // Width percentage for the filled track
    const progressPct = `${Math.min(100, (qty / maxStep) * 100)}%`;

    const track = `
      <div class="progress-line__track">
        <div class="progress-line__track-fill" style="--progress-pct: ${progressPct}"></div>
      </div>
    `;

    const blocks = `<div class="progress-blocks">${this.buildBlocks(qty, maxStep)}</div>`;
    const labels = `<div class="progress-line__labels">${this.buildTierLabels(qty, maxStep, tiers)}</div>`;

    this.setAttribute('role', 'img');
    this.setAttribute('aria-label', `Progress: ${qty} of ${maxStep}`);
    this.innerHTML = `<div class="progress-line__inner">${track}${blocks}${labels}</div>`;
  }
}

customElements.define('progress-line', ProgressLine);


